import React, { useEffect, useState } from "react";
import { Navbar, Dropdown, Image, Button } from '@themesberg/react-bootstrap';
import { Building2, LayoutGrid, ChevronDown, PanelLeftClose, Grip, House, SquareUser, Boxes } from 'lucide-react';
import { Link, useHistory, useLocation } from "react-router-dom";
import { Routes } from "../routes";
import SiteLogo from "../assets/img/final/site-logo.png";

export default ({ onMobileToggleClick }) => {
  const location = useLocation();
  const history = useHistory();
  const { state, pathname } = location;

  // Map routes to their corresponding modules
  const routeModuleMap = {
    [Routes.Company.path]: "Admin",
    [Routes.EditOrganisation.path]: "Admin",
    [Routes.AddEntities.path]: "Admin",
    [Routes.AddWorkPlaces.path]: "Admin",
    [Routes.Department.path]: "Admin",
    [Routes.Employee.path]: "Admin",
    [Routes.Brand.path]: "Inventory",
    [Routes.Category.path]: "Inventory",
    [Routes.Spusku.path]: "Inventory",
    [Routes.StockList.path]: "Inventory",
    [Routes.SkuPresets.path]: "Inventory",
    [Routes.Branch.path]: "Inventory",
    // Add Sales routes if applicable, e.g.:
    "/listing-pricing": "Sales",
    "/platform-order": "Sales",
  };

  // Initialize sectionState based on URL, location.state, or localStorage
  const getInitialSectionState = () => {
    const normalizedPath = pathname.replace(/\/+$/, '');
    // Check if the current path is in the routeModuleMap
    for (const [route, module] of Object.entries(routeModuleMap)) {
      if (normalizedPath === route || normalizedPath.startsWith(route + "/")) {
        return module;
      }
    }
    // Fallback to location.state, localStorage, or "Admin"
    return state?.section || localStorage.getItem('selectedModule') || "Admin";
  };

  const [sectionState, setSectionState] = useState(getInitialSectionState());

  // selectedProduct remains for the product dropdown label
  const [selectedProduct, setSelectedProduct] = useState(
    sectionState === "Admin" ? "Admin" : (sectionState || "Inventory")
  );

  // Keep selectedProduct and localStorage in sync when sectionState changes
  useEffect(() => {
    setSelectedProduct(sectionState === "Admin" ? "Admin" : sectionState || "Inventory");
    localStorage.setItem('selectedModule', sectionState);
    // Notify other components of module change
    window.dispatchEvent(new CustomEvent('selectedModuleChanged', { detail: sectionState }));
  }, [sectionState]);

  // Update sectionState when location changes (direct URL access or navigation)
  useEffect(() => {
    const newSection = getInitialSectionState();
    if (newSection !== sectionState) {
      setSectionState(newSection);
    }
  }, [pathname, state?.section]);

  // Listen for external module changes (dispatched by other components)
  useEffect(() => {
    const handler = (e) => {
      const newModule = e?.detail || localStorage.getItem('selectedModule') || "Admin";
      setSectionState(newModule);
    };
    window.addEventListener('selectedModuleChanged', handler);
    return () => window.removeEventListener('selectedModuleChanged', handler);
  }, []);

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setSectionState(product);
    localStorage.setItem('selectedModule', product);
    window.dispatchEvent(new CustomEvent('selectedModuleChanged', { detail: product }));
    history.push({
      pathname: Routes.DashboardOverview.path,
      state: { section: product }
    });
  };

  const user = localStorage.getItem('user');
  let auth_user = '';
  if (user) {
    auth_user = JSON.parse(user);
  }

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  // Custom Dropdown component to manage open state and toggle
  const CustomGridDropdown = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);

    const closeDropdown = () => setIsOpen(false);
    const toggleDropdown = () => setIsOpen(prev => !prev);

    return (
      <Dropdown show={isOpen} onToggle={toggleDropdown} align="end" className="grid-dropdown">
        <Dropdown.Toggle as="div" className="d-flex align-items-center cursor-pointer">
          <Grip size={24} stroke="#6C757D" />
        </Dropdown.Toggle>
        <Dropdown.Menu className="grid-dropdown-menu p-3">
          <div className="grid-items">
            <Link to={{ pathname: "/welcome", state: { section: sectionState } }} className="grid-item" onClick={closeDropdown}>
              <House size={28} />
              <span>Home</span>
            </Link>
            <Link
              to={{ pathname: Routes.DashboardOverview.path, state: { section: "Admin" } }}
              className="grid-item"
              onClick={() => {
                localStorage.setItem("selectedModule", "Admin");
                window.dispatchEvent(new CustomEvent('selectedModuleChanged', { detail: "Admin" }));
                closeDropdown();
              }}
            >
              <SquareUser size={28} />
              <span>Admin</span>
            </Link>
            <Link
              to={{ pathname: Routes.DashboardOverview.path, state: { section: "Inventory" } }}
              className="grid-item"
              onClick={() => {
                localStorage.setItem("selectedModule", "Inventory");
                window.dispatchEvent(new CustomEvent('selectedModuleChanged', { detail: "Inventory" }));
                closeDropdown();
              }}
            >
              <Boxes size={28} />
              <span>Inventory</span>
            </Link>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  return (
    <Navbar className="bg-white p-2 w-100 justify-content-between" expand="md">
      <div className="d-flex align-items-center justify-content-between w-100 gap-4 gap-md-0">
        <Button variant="light" onClick={onMobileToggleClick} className="toggle-btn d-md-none">
          <PanelLeftClose />
        </Button>
        <div className="d-flex align-items-center justify-content-around justify-content-md-start nav-btns" style={{ flex: 1 }}>
          {sectionState === "Admin" ? (
            <Button className="d-flex align-items-center me-2 text-quaternary topbar-btn btn-white">
              Admin
            </Button>
          ) : (
            <>
              <Button className="d-flex align-items-center me-2 text-quaternary topbar-btn btn-white">
                ERP
              </Button>
              <Dropdown>
                <Dropdown.Toggle variant="light" className="d-flex align-items-center cursor-pointer product-manage" as="div">
                  {selectedProduct} <ChevronDown className="ms-1" size={20} />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => { handleProductSelect("Inventory"); }}>
                    Inventory
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => { handleProductSelect("Sales"); }}>
                    Sales
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </>
          )}
        </div>
        <div className="nav-logo" style={{ display: 'none' }}>
          <Link to={{ pathname: Routes.DashboardOverview.path, state: { section: sectionState } }}>
            <Image src={SiteLogo} height={32} />
          </Link>
        </div>

        <Dropdown align="end" className="profile-menu d-flex align-items-center">
          <CustomGridDropdown />

          <Dropdown.Toggle as="div" className="d-flex align-items-center cursor-pointer ms-3">
            <div className="avatar bg-secondary text-white rounded-circle d-flex justify-content-center align-items-center" style={{ width: 32, height: 32 }}>
              {auth_user?.userDetails?.employee_first_name?.[0]}{auth_user?.userDetails?.employee_last_name?.[0]}
            </div>
          </Dropdown.Toggle>
          <Dropdown.Menu className="user-dropdown">
            <Dropdown.Item>My Profile</Dropdown.Item>
            <Dropdown.Item>Settings</Dropdown.Item>
            <Dropdown.Item onClick={handleLogout}>Logout</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </Navbar>
  );
};